import { useEffect, useMemo, useState } from "react";
import AutoSeo from "@/components/AutoSeo";
import PageMeta from "@/components/PageMeta";
import api from "@/lib/api";
import Media from "@/components/Media";

const ALPHA_KEYS = ["0-9", "A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z"];

// Normalise an admin-pasted URL so a clicked card actually navigates.
// People paste "amazon.co.uk/dp/..." without the scheme; that becomes a same-page navigation
// (href="amazon.co.uk/..." resolves relative to current origin) and feels like "the link does nothing".
function normaliseUrl(raw) {
  if (!raw || typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  // Guard against garbage admin pastes like "#" or "n/a" — require at least one '.'
  // (so we don't render <a href="https://#">).
  const candidate = trimmed.replace(/^\/+/, "");
  if (!candidate.includes(".")) return "";
  return `https://${candidate}`;
}

function firstChar(title) {
  const t = (title || "").trim();
  if (!t) return "#";
  const c = t[0].toUpperCase();
  if (/[0-9]/.test(c)) return "0-9";
  if (/[A-Z]/.test(c)) return c;
  return "#";
}

// Words we never want to surface as a "popular search" chip.
const CHIP_STOPWORDS = new Set([
  "the","and","of","for","with","in","on","a","an","to","by","from","or","de","la","le","du",
  "premium","classic","original","traditional","best","new","fine","pack","set","pro",
  "small","medium","large","xl","mini","kg","g","ml","l","oz","cl","pcs","piece","pieces",
  "no","brand","co","ltd","inc","uk","us","eu",
]);

// Extract the top ~5 keywords from titles so the chip row stays relevant to
// whatever the admin has curated in each category. We keep it purely derived
// (no hard-coded list) so chips auto-refresh as inventory changes.
function derivePopularTerms(items, limit = 6) {
  const counts = new Map();
  items.forEach((it) => {
    const words = String(it.title || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s'-]/g, " ")
      .split(/\s+/)
      .filter(Boolean);
    words.forEach((w) => {
      if (w.length < 3) return;
      if (/^\d+$/.test(w)) return;
      if (CHIP_STOPWORDS.has(w)) return;
      counts.set(w, (counts.get(w) || 0) + 1);
    });
  });
  return [...counts.entries()]
    .filter(([, n]) => n >= 2) // must appear in at least 2 items to feel "popular"
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([w]) => w.charAt(0).toUpperCase() + w.slice(1));
}

// Fire a shop event without blocking the user's navigation. Uses sendBeacon when
// available (survives page unload for outbound item clicks); falls back to a
// keepalive fetch. Errors are swallowed — analytics must never break the flow.
function trackShopEvent(payload) {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/shop/track`;
    const body = JSON.stringify(payload);
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}

export default function Shop() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("tools");
  const [visible, setVisible] = useState(8);
  const [letter, setLetter] = useState(null);  // active A-Z quick filter
  const [query, setQuery] = useState("");       // free-text search
  const [activeChip, setActiveChip] = useState(null); // last chip clicked — attached to item_click for conversion attribution

  useEffect(() => {
    api.get(`/shop?category=${tab}`).then((r) => setItems(r.data));
    setVisible(8);
    setLetter(null); // reset alpha filter when switching tabs
    setQuery("");    // reset search when switching tabs
    setActiveChip(null);
  }, [tab]);

  // Items after applying the free-text search (used by A-Z strip + final list).
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = `${it.title || ""} ${it.summary || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  // Which alpha keys actually have items? Reflects the current search so A-Z disables
  // letters that would produce zero matches given the query.
  const availableLetters = useMemo(() => {
    const set = new Set();
    searched.forEach((it) => set.add(firstChar(it.title)));
    return set;
  }, [searched]);

  const filtered = letter ? searched.filter((it) => firstChar(it.title) === letter) : searched;
  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  // Popular-search chips derived from the current tab's inventory. Hidden while the user
  // is already typing so we don't compete with their query.
  const popularTerms = useMemo(() => derivePopularTerms(items), [items]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
      <AutoSeo
        pageKey="shop"
        fallbackTitle="Shop — aprons, prints & cookbooks"
        fallbackDescription="Hand-picked Recipe Filer goods — bound family cookbooks, kitchen prints, and aprons that celebrate the cooks behind every recipe."
      />
      <p className="overline mb-4">Shop</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-10 leading-[0.95]">Kit and pantry,<br />vetted by cooks.</h1>

      {/* Category tabs */}
      <div className="flex bg-white rounded-full p-1 border border-rf-ink/10 mb-8 w-fit">
        <button
          data-testid="shop-tab-tools"
          onClick={() => setTab("tools")}
          className={`px-6 py-2.5 rounded-full text-sm font-bold transition-colors ${tab === "tools" ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft"}`}
        >Tools</button>
        <button
          data-testid="shop-tab-food"
          onClick={() => setTab("food_drink")}
          className={`px-6 py-2.5 rounded-full text-sm font-bold transition-colors ${tab === "food_drink" ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft"}`}
        >Food &amp; Drink</button>
      </div>

      {/* Search box — filters by title/summary, works alongside A-Z */}
      <div className="mb-6 max-w-md" data-testid="shop-search-wrap">
        <label htmlFor="shop-search" className="sr-only">Search shop items</label>
        <div className="relative">
          <svg
            aria-hidden="true"
            className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-rf-ink-soft"
            fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="7" />
            <path strokeLinecap="round" d="m20 20-3.5-3.5" />
          </svg>
          <input
            id="shop-search"
            type="search"
            value={query}
            onChange={(e) => {
              const v = e.target.value;
              setQuery(v);
              setVisible(8);
              setLetter(null);
              // Manual typing shouldn't inherit chip attribution unless the query
              // still matches the last chip (case-insensitive).
              if (!activeChip || v.toLowerCase() !== activeChip.toLowerCase()) {
                setActiveChip(null);
              }
            }}
            placeholder="Search items…"
            data-testid="shop-search-input"
            className="w-full pl-11 pr-10 py-2.5 rounded-full bg-white border border-rf-ink/10 text-sm text-rf-ink placeholder:text-rf-ink-soft/70 focus:outline-none focus:border-rf-orange focus:ring-1 focus:ring-rf-orange [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
          />
          {query && (
            <button
              type="button"
              data-testid="shop-search-clear"
              onClick={() => { setQuery(""); setVisible(8); setActiveChip(null); }}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-rf-cream-alt text-rf-ink-soft hover:text-rf-ink hover:bg-rf-ink/10 flex items-center justify-center text-sm"
            >
              ×
            </button>
          )}
        </div>
        {query && (
          <p className="text-xs text-rf-ink-soft mt-2" data-testid="shop-search-active">
            {searched.length} match{searched.length === 1 ? "" : "es"} for <b>&ldquo;{query}&rdquo;</b>
          </p>
        )}
        {!query && popularTerms.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="shop-popular-chips">
            <span className="text-xs text-rf-ink-soft mr-1">Popular:</span>
            {popularTerms.map((term) => (
              <button
                key={term}
                type="button"
                data-testid={`shop-popular-${term.toLowerCase()}`}
                onClick={() => {
                  setQuery(term);
                  setLetter(null);
                  setVisible(8);
                  setActiveChip(term);
                  trackShopEvent({ event_type: "chip_click", chip: term, category: tab });
                }}
                className="px-3 py-1 rounded-full text-xs font-semibold bg-white text-rf-ink border border-rf-ink/10 hover:border-rf-orange hover:text-rf-orange transition-colors"
              >
                {term}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* A-Z quick directory */}
      <div className="mb-10" data-testid="shop-az-strip">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            data-testid="shop-az-all"
            onClick={() => { setLetter(null); setVisible(8); }}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${letter === null ? "bg-rf-orange text-white" : "bg-white text-rf-ink-soft border border-rf-ink/10 hover:border-rf-orange"}`}
          >All</button>
          {ALPHA_KEYS.map((k) => {
            const hasItems = availableLetters.has(k);
            return (
              <button
                key={k}
                data-testid={`shop-az-${k}`}
                disabled={!hasItems}
                onClick={() => { setLetter(k); setVisible(8); }}
                className={`w-9 h-9 rounded-full text-xs font-bold transition-all ${
                  letter === k
                    ? "bg-rf-orange text-white"
                    : hasItems
                    ? "bg-white text-rf-ink border border-rf-ink/10 hover:border-rf-orange hover:text-rf-orange"
                    : "bg-rf-cream-alt text-rf-ink-soft/40 cursor-not-allowed border border-transparent"
                }`}
                aria-pressed={letter === k}
              >{k === "0-9" ? "#" : k}</button>
            );
          })}
        </div>
        {letter && (
          <p className="text-xs text-rf-ink-soft mt-3" data-testid="shop-az-active">
            Showing items starting with <b>{letter === "0-9" ? "0-9" : letter}</b> · {filtered.length} match{filtered.length === 1 ? "" : "es"}
          </p>
        )}
      </div>

      {/* Items grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {shown.map((it) => {
          const href = normaliseUrl(it.external_url);
          const hasLink = !!href;
          // When the URL is missing we render a non-clickable card with a "No link" badge
          // so users get visual feedback instead of "the click reverts back to the Shop page".
          const Wrapper = hasLink ? "a" : "div";
          const wrapperProps = hasLink
            ? {
                href,
                target: "_blank",
                rel: "noopener noreferrer nofollow sponsored",
                onClick: () => trackShopEvent({
                  event_type: "item_click",
                  item_id: it.item_id,
                  category: tab,
                  chip: activeChip || null,
                }),
              }
            : { role: "group", "aria-label": `${it.title} (no link)` };
          return (
            <Wrapper
              key={it.item_id}
              {...wrapperProps}
              data-testid={`shop-item-${it.item_id}`}
              className={`card-recipe ${hasLink ? "hover-lift cursor-pointer" : "opacity-90"} block overflow-hidden relative`}
            >
              {it.on_sale && (
                <div className="absolute top-3 right-3 z-10 bg-rf-orange text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  {it.sale_text || "Sale"}
                </div>
              )}
              {!hasLink && (
                <div className="absolute top-3 left-3 z-10 bg-rf-ink/80 text-rf-cream text-xs font-bold px-3 py-1.5 rounded-full">
                  No link
                </div>
              )}
              {it.image_url && (
                <div className="-m-6 mb-4 aspect-square overflow-hidden">
                  <Media src={it.image_url} alt={it.title} className="w-full h-full object-cover" />
                </div>
              )}
              <h3 className="font-display font-extrabold text-lg text-rf-ink mb-2 leading-tight">{it.title}</h3>
              <p className="text-rf-ink-soft text-sm line-clamp-3">{it.summary}</p>
            </Wrapper>
          );
        })}
      </div>

      {shown.length === 0 && (
        <div className="card-recipe text-center py-12 mt-2" data-testid="shop-empty">
          <p className="text-rf-ink-soft">
            No items
            {letter ? <> starting with <b>{letter === "0-9" ? "0-9" : letter}</b></> : null}
            {query ? <> matching <b>&ldquo;{query}&rdquo;</b></> : null}
            {" "}in this category.
          </p>
          <button
            onClick={() => { setLetter(null); setQuery(""); }}
            data-testid="shop-empty-reset"
            className="text-rf-orange font-bold mt-2 hover:underline"
          >
            Show all
          </button>
        </div>
      )}

      {hasMore && (
        <div className="text-center mt-12">
          <button
            data-testid="shop-show-more"
            onClick={() => setVisible((v) => v + 8)}
            className="btn-ink"
          >
            Show more ({filtered.length - visible} remaining)
          </button>
        </div>
      )}
      <p className="text-xs text-rf-ink-soft mt-12">Some links earn us a small commission at no cost to you.</p>
    </div>
  );
}
